// The HEALTH tier, as the browser sees it (PR 2 of the LTC health intake
// program; the server half is workers/api/src/middleware/require-health-
// access.ts, and its header is the authoritative statement of who may
// open what).
//
// Three things live here, all plain TypeScript so a node-pool test can
// cover them without booting React:
//
//   1. The refusal the server sends (HealthAccessDenial) and a reader that
//      recognizes it on an ApiError, duck-typed so this module never
//      imports api.ts (api.ts imports it).
//   2. The four reasons an administrator can give, their labels, and the
//      one validation rule ("Something else" needs a note).
//   3. The stated reason, remembered IN MEMORY per household for 30
//      minutes so one reason opens every list on the family's screens
//      and none on any other family's. Never browser storage: a reason is
//      an assertion about right now, and it must not survive a reload,
//      reach another tab, or be read back later by anyone.
//
// DECISION: keyed by household, not by person. The server decides per
// household (the tier is defined over the household's Planning
// relationship), so a reason given on Karen's card is the same answer
// for Dale's, for the case page, and for the worksheet. Asking again per
// person would be friction with no added honesty: the audit row and the
// timeline notice are still written per person.

export type HealthAccessReasonCode =
  | "client_asked_directly"
  | "covering_for_producer"
  | "compliance_review"
  | "other";

export interface HealthAccessReason {
  code: HealthAccessReasonCode;
  note: string | null;
}

/** Mirror of the server's HealthAccessDenialBody. */
export interface HealthAccessDenial {
  error: "health_access_denied";
  held_by: "planning";
  reason: "role_denied" | "scope_denied" | "reason_required" | "reason_invalid";
  break_glass_available: boolean;
  household_id: string | null;
  contact_id: string | null;
  ask: { user_id: string; name: string | null; email: string | null } | null;
  message: string;
  path_forward: string;
}

/** Same cap as the server's HEALTH_ACCESS_NOTE_MAX. */
export const HEALTH_ACCESS_NOTE_MAX = 200;

/** How long a stated reason keeps a household open in this tab. */
export const HEALTH_ACCESS_REASON_TTL_MS = 30 * 60 * 1000;

/** The dialog's four choices, in the order the mockup draws them. The
 *  labels read as the admin speaking; the server writes its own shorter
 *  label onto the timeline row. */
export const HEALTH_ACCESS_REASON_CHOICES: ReadonlyArray<{
  code: HealthAccessReasonCode;
  label: (producer: string) => string;
}> = [
  {
    code: "client_asked_directly",
    label: (p) => `The client asked me directly and ${p} is not available`,
  },
  { code: "covering_for_producer", label: (p) => `Covering for ${p}` },
  { code: "compliance_review", label: () => "Compliance or complaint review" },
  { code: "other", label: () => "Something else (say what)" },
];

/** Recognize the health tier's 403 on anything thrown by the API client.
 *  Any other error (a 403 for a different reason, a 500, a network
 *  failure) is null, so callers keep their existing handling for it. */
export function readHealthAccessDenial(err: unknown): HealthAccessDenial | null {
  if (!err || typeof err !== "object") return null;
  const e = err as { status?: unknown; data?: unknown };
  if (e.status !== 403) return null;
  const data = e.data as Partial<HealthAccessDenial> | null | undefined;
  if (!data || data.error !== "health_access_denied") return null;
  return {
    error: "health_access_denied",
    held_by: "planning",
    reason: data.reason ?? "scope_denied",
    break_glass_available: data.break_glass_available === true,
    household_id: typeof data.household_id === "string" ? data.household_id : null,
    contact_id: typeof data.contact_id === "string" ? data.contact_id : null,
    ask: data.ask ?? null,
    message:
      data.message ??
      "Health information for this person is held by Stewardship Planning and is not shown outside it.",
    path_forward: data.path_forward ?? "Ask the Planning team.",
  };
}

/** Validate what the admin picked and typed. The server re-checks all of
 *  this; this only keeps the button honest about whether it will work. */
export function validateHealthReasonDraft(
  code: HealthAccessReasonCode | null,
  note: string,
): { ok: true; reason: HealthAccessReason } | { ok: false; message: string } {
  if (!code) return { ok: false, message: "Pick a reason." };
  const trimmed = note.trim().slice(0, HEALTH_ACCESS_NOTE_MAX);
  if (code === "other" && trimmed === "") {
    return { ok: false, message: "Say what the reason is." };
  }
  return { ok: true, reason: { code, note: trimmed === "" ? null : trimmed } };
}

/** Append the stated reason to an API path. No reason → the path
 *  unchanged, which is every request from anyone the tier already lets
 *  in. Handles a path that already carries a query string. */
export function withHealthAccess(
  path: string,
  access: HealthAccessReason | null | undefined,
): string {
  if (!access) return path;
  const params = new URLSearchParams({ access_reason: access.code });
  if (access.note) params.set("access_note", access.note);
  return `${path}${path.includes("?") ? "&" : "?"}${params.toString()}`;
}

// ── The in-memory store ──────────────────────────────────────────────

const reasons = new Map<string, { reason: HealthAccessReason; at: number }>();
const listeners = new Set<() => void>();

export function getHealthAccessReason(
  householdId: string | null | undefined,
  now: number = Date.now(),
): HealthAccessReason | null {
  if (!householdId) return null;
  const hit = reasons.get(householdId);
  if (!hit) return null;
  if (now - hit.at > HEALTH_ACCESS_REASON_TTL_MS) {
    reasons.delete(householdId);
    return null;
  }
  return hit.reason;
}

export function setHealthAccessReason(
  householdId: string,
  reason: HealthAccessReason,
  now: number = Date.now(),
): void {
  reasons.set(householdId, { reason, at: now });
  for (const l of listeners) l();
}

export function subscribeHealthAccessReasons(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam only. */
export function clearHealthAccessReasons(): void {
  reasons.clear();
  for (const l of listeners) l();
}

/** A stable string for effect dependencies: "" when no reason, else the
 *  code and note, so a list re-fetches the moment a reason is stated. */
export function healthAccessKey(access: HealthAccessReason | null | undefined): string {
  return access ? `${access.code}:${access.note ?? ""}` : "";
}
